<!-- /* * Confidential and Proprietary. * Do not distribute without 1-800-Flowers.com, Inc. consent. *
Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved. */ -->

<div class="experience-rating-container">
	<div>
		{#if showExperiencePrompt}
			<p class="rating-prompt">How would you rate your chat experience today?</p>
			<div class="rating-scale">
				{#each [1, 2, 3, 4, 5] as score}
					<div class="rating-star-wrapper">
						<button
							class="rating-star {hoveredRating >= score ? 'active' : ''}"
							on:click={() => submitExperienceRating(score)}
							on:mouseover={() => (hoveredRating = score)}
							on:mouseout={() => (hoveredRating = 0)}
							on:focus={() => (hoveredRating = score)}
							on:blur={() => (hoveredRating = 0)}
							aria-label={`Rating ${score}`}
						>
							<span class="rating-label">
								{#if hoveredRating >= score}
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 24 24"
										width="24"
										height="24"
										fill="#FFD700"
										aria-hidden="true"
									>
										<path
											d="M12 .587l3.668 7.568L24 9.748l-6 5.848L19.335 24 12 20.201 4.665 24 6 15.596 0 9.748l8.332-1.593z"
										/>
									</svg>
								{:else if hoveredRating < score}
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 24 24"
										width="24"
										height="24"
										fill="rgba(255, 215, 0, 0.3)"
										stroke="#FFD700"
										stroke-width="2"
										stroke-linecap="round"
										stroke-linejoin="round"
									>
										<polygon
											points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
										/>
									</svg>
								{/if}
							</span>
						</button>
						{#if hoveredRating === score}
							<div class="tooltip-experience">
								{#if score === 1}
									Not satisfied
								{:else if score === 2}
									Slightly satisfied
								{:else if score === 3}
									Neutral
								{:else if score === 4}
									Satisfied
								{:else if score === 5}
									Extremely satisfied
								{/if}
							</div>
						{/if}
					</div>
				{/each}
			</div>
		{:else}
			<div class="rating-secondary">
				<p class="rating-prompt-secondary">
					{scoreMessage < 4 ? surveyMessages[0] : surveyMessages[1]}
				</p>
				<a class="survey-button" {href} target="_blank" rel="noopener noreferrer">
					Share More Feedback
				</a>
			</div>
		{/if}
	</div>
</div>

<script lang="ts">
	import { sendSignal } from '$lib/apis/webchat/sendSignal';
	import { chatSession, surveySent } from '$lib/stores/chatSessionStore';
	import { onMount } from 'svelte';
	import { socketDisconnect } from '$lib/stores/websocketStore';
	import { datadogLogs } from '@datadog/browser-logs';

	$: scoreMessage = clickedScore;
	export let href = '';

	let showExperiencePrompt = true;
	let hoveredRating = 0;
	let clickedScore = 0;
	const surveyMessages = [
		'We\’re sorry to hear that. Can you tell us what went wrong?',
		'Glad to hear that! Want to tell us what went well?'
	];

	const setSurvey = async () => {
		socketDisconnect();
		surveySent.set(true);
	};

	onMount(() => {
		const mount = async () => {
			await setSurvey();
		};
		mount();
	});

	const submitExperienceRating = async (score: number) => {
		showExperiencePrompt = false;
		clickedScore = score;
		datadogLogs.logger.info(
			`[Conv-ui] Quick CSAT submitted - brand: ${$chatSession.brand} channel: ${$chatSession.channel} browser_uuid: ${$chatSession.browseruuid} conversation_id: ${$chatSession?.conversation_id} score: ${score}`
		);
		await sendSignal(
			{
				conversation_id: $chatSession.conversation_id,
				brand_code: $chatSession.brand,
				channel: $chatSession.channel,
				type: 'csat',
				params: {
					agent: $chatSession.agent,
					csat_score: score,
					customer: {
						email: $chatSession.email,
						contact_id: $chatSession.contactId,
						browser_uuid: $chatSession.browseruuid,
						first: $chatSession.firstName,
						last: $chatSession.lastName
					}
				}
			},
			$chatSession.token
		);
	};
</script>

<style>
	.experience-rating-container {
		align-items: center;
		justify-content: space-between;
		padding-top: 1px;
		border-radius: 5px;
		box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.1);
		background-color: #f5f5f5;
		width: 80%;
		margin: 10px auto;
		text-align: center;
	}

	.rating-prompt {
		font-family: area-variable, sans-serif;
		font-style: normal;
		font-variation-settings:
			'INKT' 0,
			'slnt' 0,
			'wdth' 100,
			'wght' 100;
		font-size: 13.5px;
		font-weight: bold;
		color: #333;
	}

	.rating-secondary {
		align-items: center;
		padding: 7px;
		background-color: #f5f5f5;
		border-radius: 5px;
	}
	.rating-prompt-secondary {
		font-family: area-variable, sans-serif;
		font-style: normal;
		font-variation-settings:
			'INKT' 0,
			'slnt' 0,
			'wdth' 100,
			'wght' 100;
		font-size: 13px;
		font-weight: 700;
		color: #333;
		flex: 1;
		margin-left: 10px;
		text-align: center;
	}

	.rating-scale {
		display: flex;
		justify-content: center;
	}

	.rating-star-wrapper {
		position: relative;
	}

	.rating-star {
		background: transparent;
		border: none;
		cursor: pointer;
		opacity: 0.6;
		transition: opacity 0.6s ease;
		font-size: 24px;
		color: #3a3a3a;
		font-weight: bold;
		width: 40px;
		height: 40px;
	}

	.rating-star:hover,
	.rating-star:focus,
	.rating-star.active {
		opacity: 1;
		outline: none;
	}

	.tooltip-experience {
		position: absolute;
		top: -40%;
		left: 50%;
		transform: translateX(-50%);
		background-color: #333;
		color: #fff;
		padding: 4px 8px;
		border-radius: 4px;
		font-size: 12px;
		white-space: nowrap;
		opacity: 1;
		transition: opacity 0.2s ease;
		z-index: 5;
		font-family: area-variable, sans-serif;
		font-style: normal;
		font-variation-settings:
			'INKT' 0,
			'slnt' 0,
			'wdth' 100,
			'wght' 100;
		font-size: 13.5px;
		font-weight: bold;
	}
	.survey-button {
		display: block;
		margin: 24px auto 0 auto;
		padding: 10px 15px;
		background-color: #201648;
		color: white;
		font-size: 16px;
		font-weight: 400;
		text-align: center;
		text-decoration: none;
		border-radius: 5px;
		width: fit-content;
		font-family: area-variable, sans-serif;
		font-style: normal;
		font-variation-settings:
			'INKT' 0,
			'slnt' 0,
			'wdth' 100,
			'wght' 100;
	}
</style>
